import { PortableText, type PortableTextBlock } from 'next-sanity';

import { cn } from '#/lib/utils';
import { Link } from '#/components/Link';

type Props = {
  value: PortableTextBlock[];
  className?: string;
  dataSanity?: string;
};

/**
 * Renderer for the `formattedText` schema: paragraphs, bullet/number lists,
 * bold, italic, superscript/subscript, links and anchor IDs. Text colour and
 * size are intentionally inherited from the parent so the same content reads
 * correctly on any themed surface (light or dark).
 */
export function FormattedText({ value, className, dataSanity }: Props) {
  return (
    <div
      data-sanity={dataSanity}
      className={cn('flex flex-col gap-8', className)}
    >
      <PortableText
        value={value}
        components={{
          list: {
            bullet: ({ children }) => (
              <ul className="ml-[1.4em] flex list-disc flex-col gap-4">
                {children}
              </ul>
            ),
            number: ({ children }) => (
              <ol className="ml-[1.4em] flex list-decimal flex-col gap-4">
                {children}
              </ol>
            ),
          },
          listItem: {
            bullet: ({ children }) => <li className="pl-4">{children}</li>,
            number: ({ children }) => <li className="pl-4">{children}</li>,
          },
          marks: {
            strong: ({ children }) => (
              <strong className="font-medium">{children}</strong>
            ),
            em: ({ children }) => <em className="italic">{children}</em>,
            sup: ({ children }) => <sup>{children}</sup>,
            sub: ({ children }) => <sub>{children}</sub>,
            internalLink: ({ value, children }) => (
              <Link
                className="underline decoration-1 underline-offset-2 transition-opacity hover:opacity-70"
                href={value.href}
                target={value.target}
              >
                {children}
              </Link>
            ),
            externalLink: ({ value, children }) => (
              <Link
                className="underline decoration-1 underline-offset-2 transition-opacity hover:opacity-70"
                href={value.href}
                target={value.target}
              >
                {children}
              </Link>
            ),
            anchorId: ({ value, children }) => (
              <span id={value.id}>{children}</span>
            ),
          },
        }}
      />
    </div>
  );
}
